import { IGeo, ISocialPlatform } from 'interfaces'
import { Timestamp } from '../interfaces/user'
import { AnalyticsSocialNetworkTypes } from 'containers/analytics/provisional/types/analytics'
import { isString } from '@tiptap/react'

/**
 * Calculates the age based on the given birthdate.
 *
 * @param {Date} birthdate - The birthdate to calculate the age from.
 * @returns {number} The calculated age.
 */
export const calculateAge = (birthdate: Date): number => {
  const today = new Date()
  let age = today.getFullYear() - birthdate.getFullYear()
  const m = today.getMonth() - birthdate.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birthdate.getDate())) {
    age--
  }

  return age
}

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export interface FollowersCountModel {
  value: number | string
  key: string
}

// Get date by timestamp format month/YYYY
export const getDateByTimestamp = (timestamp: Timestamp) => {
  const date = new Date(timestamp._seconds * 1000)
  return `${monthNames[date.getMonth() + 1]} ${date.getFullYear()}`
}

/**
 * Get's a date string for a UNIX timestamp int
 *
 * @param {number} date - UNIX timestamp int
 * @returns {string} - January 4, 2024
 */
export const getFullPlainTextDate = (date: number): string => {
  const dateF = new Date(date * 1000)
  const day = dateF.getDate()
  const month = dateF.toLocaleString('default', { month: 'short' })
  const year = dateF.getFullYear().toString()

  return `${month} ${day}, ${year}`
}

export const calculateFollowers = (
  followers: number,
  removeKey = false
): FollowersCountModel => {
  if (isString(followers)) {
    followers = parseInt(followers)
  }

  if (followers < 100000) {
    return {
      value: thousandSeparator(removeDecimals(followers.toFixed(2))),
      key: '',
    }
  }

  if (followers < 1000000) {
    const result = followers / 1000
    return {
      value: thousandSeparator(removeDecimals(result.toFixed(2))),
      key: removeKey ? '' : 'K',
    }
  }

  const result = followers / 1000000

  return {
    value: thousandSeparator(removeLastNumberIfIsZero(result.toFixed(2))),
    key: removeKey ? '' : 'M',
  }
}

export const formatNumberWithSuffix = (value: number) => {
  const formatedNumber = calculateFollowers(value)
  return `${formatedNumber.value}${formatedNumber.key}`
}

export const removeDecimals = (number: string): string => {
  return removeLastNumberIfIsZero(number)
}

export const removeLastNumberIfIsZero = (number: string): string => {
  if (number.length === 1 || Number(number) % 1 === 0) {
    return Number(number).toFixed(0)
  }
  const lastNumber = number.slice(-1)
  if (lastNumber === '0') {
    return number.slice(0, -1)
  }
  return number
}

export const thousandSeparator = (number: number | string): string => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const generateRandomUniqueID = (sub?: string) => {
  return Math.random().toString(36).slice(2, 9) + (sub || '')
}

export const formatLocation = (location: IGeo) => {
  return `${location.city}, ${location.country}`
}

export const setCookie = (
  name: string,
  value: string,
  expirationDays?: number
) => {
  let expires = ''
  if (expirationDays) {
    const date = new Date()
    date.setTime(date.getTime() + expirationDays * 24 * 60 * 60 * 1000)
    expires = `; expires=${date.toUTCString()}`
  }
  if (typeof window !== 'undefined') {
    document.cookie = `${name}=${value || ''}${expires}; path=/`
  }
}

export const getCookie = (name: string, fromValue?: string) => {
  if (typeof window !== 'undefined') {
    const value = `; ${fromValue ? fromValue : document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop()?.split(';').shift()
  } else if (fromValue) {
    const value = `; ${fromValue}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop()?.split(';').shift()
  }
}

export const getCookieToken = () => {
  if (typeof window !== 'undefined') {
    const value = `; ${document.cookie}`
    const parts = value.split(`; token=`)
    if (parts.length === 2) return parts.pop()?.split(';').shift() || ''
  }
}

export const removeCookie = (name: string) => {
  if (typeof window !== 'undefined') {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`
  }
}

export const getAuthMethodByLabel = (label: 'SMS' | 'EMAIL') => {
  return label === 'SMS' ? 'mobile' : 'email'
}

export const calculateTimePassed = (date: number) => {
  const now = new Date()
  const past = new Date(date)
  const diff = Math.abs(now.getTime() - past.getTime())
  const hours = Math.ceil(diff / (1000 * 3600))
  const days = Math.ceil(diff / (1000 * 3600 * 24))
  const months = Math.ceil(diff / (1000 * 3600 * 24 * 30))
  const years = Math.ceil(diff / (1000 * 3600 * 24 * 30 * 12))
  if (hours < 24) {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
  } else if (days < 30) {
    return `${days} ${days === 1 ? 'day' : 'days'} ago`
  } else if (months < 12) {
    return `${months} ${months === 1 ? 'month' : 'months'} ago`
  } else {
    return `${years} ${years === 1 ? 'year' : 'years'} ago`
  }
}

export const getYoutubeDuration = (duration: string) => {
  const hours = duration.match(/(\d+)H/)
  const minutes = duration.match(/(\d+)M/)
  const seconds = duration.match(/(\d+)S/)
  const hoursString = hours ? `${hours[1]}:` : ''
  const minutesString = minutes ? `${minutes[1]}:` : '00:'
  const secondsString = seconds ? `${seconds[1]}` : '00'
  return `${hoursString}${minutesString}${secondsString}`
}

export const getYoutubeTimeFromSeconds = (duration: number) => {
  const hours = Math.floor(duration / 3600)
  const minutes = Math.floor((duration - hours * 3600) / 60)
  const seconds = duration - hours * 3600 - minutes * 60
  const hoursString = hours ? `${hours}:` : ''
  const minutesString = minutes ? `${minutes}:` : '00:'
  const secondsString = seconds ? `${seconds}` : '00'
  return `${hoursString}${minutesString}${secondsString}`
}

export const getSocialName = (
  social: ISocialPlatform | AnalyticsSocialNetworkTypes
) => {
  const mapper = {
    facebook: 'Facebook',
    twitter: 'Twitter',
    instagram: 'Instagram',
    instagramBasic: 'Instagram',
    linkedin: 'LinkedIn',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    telegram: 'Telegram',
    discord: 'Discord',
    twitch: 'Twitch',
    pinterest: 'Pinterest',
    snapchat: 'Snapchat',
    onlyfans: 'OnlyFans',
  }

  return mapper[social]
}
